import Vue from 'vue';
import { createFrozenMap } from '@/vuex/utils';
import {
    addGuideToCategory,
    removeGuideFromCategory,
    fetchGuideCategories,
    fetchGuideCategory,
    createGuideCategory,
    updateGuideCategory,
    deleteGuideCategory,
    sortByName,
} from './guide-categories.utils';
import { localizeDefaultGuideCategories } from './guide-categories.utils';
import { i18n_t as $t } from '@utils/i18n';

const loadAllPromise = {
    promise: null,
    items: false,
};

const getCleanedUpCategory = (category, guideId) => {
    return {
        ...category,
        items: category?.items.filter((guide) => guide.itemId !== guideId) || [],
    };
};

const createUnassignedMetaGroup = (items = []) => ({
    name: $t('metaGroups.Custom'),
    id: '_PENDO_UNASSIGNED_META_GROUP_',
    description: 'Unassigned categories',
    length: items.length,
    type: 'metaGroup',
    items: sortByName(items),
});

const guideCategories = {
    namespaced: true,
    state: {
        map: {},
        fetching: false,
        saving: false,
        error: null,
        groupExpandedKeys: [],
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { guideCategory }) {
            Vue.set(state.map, guideCategory.id, guideCategory);
        },
        unsetMapAtKey (state, id) {
            delete state.map[id];
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setSaving (state, { saving }) {
            state.saving = saving;
        },
        setPairedCategories (state, categories) {
            categories.forEach((category) => {
                if (category.type === 'guideCategory') {
                    state.map[category.id] = category;
                }
            });
        },
        setGroupExpandedKeys (state, { groupExpandedKeys }) {
            state.groupExpandedKeys = groupExpandedKeys;
        },
    },
    actions: {
        async loadAll ({ commit }, { reFetch = false, items = false } = {}) {
            // If we have already fetched with items, don't fetch again
            // If we have not already fetched with items, send a new fetch
            if (loadAllPromise.promise) {
                await loadAllPromise.promise;

                if (!reFetch && (loadAllPromise.items || !items)) {
                    return;
                }
            }
            commit('setFetching', { fetching: true });
            loadAllPromise.items = items;
            loadAllPromise.promise = fetchGuideCategories({ items });

            try {
                const { data: categories } = await loadAllPromise.promise;

                const localizedCategories = localizeDefaultGuideCategories(categories);
                commit('setFetching', { fetching: false });
                commit('setMap', {
                    map: createFrozenMap(localizedCategories),
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async loadOne ({ commit, getters }, { reFetch = false, items = false, categoryId }) {
            if (!reFetch && !!getters.guideCategoryById(categoryId)) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const { data: guideCategory } = await fetchGuideCategory({ categoryId, items });
                const localizedGuideCategory = localizeDefaultGuideCategories([guideCategory])[0];
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', {
                    guideCategory: Object.freeze(localizedGuideCategory),
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async createCategory ({ commit }, payload = {}) {
            commit('setFetching', { fetching: true });

            try {
                const { data: categories } = await createGuideCategory({
                    type: 'guideCategory',
                    ...payload,
                });

                // Update the category map with all related categories affected by this create.
                commit('setPairedCategories', categories);

                return categories;
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async updateCategory ({ commit }, payload = {}) {
            commit('setFetching', { fetching: true });

            try {
                const { data: categories } = await updateGuideCategory(payload);

                // Update the category map with all related categories affected by this update.
                commit('setPairedCategories', categories);

                return categories;
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async deleteCategory ({ commit, getters }, id) {
            commit('setFetching', { fetching: true });

            try {
                await deleteGuideCategory(id, 'guideCategory');

                const toRemove = getters.guideCategoryById(id);
                const deleted = [];

                if (toRemove) {
                    const toRemoveName = toRemove.name.toLowerCase();
                    getters.list.forEach((category) => {
                        if (category.name.toLowerCase() === toRemoveName) {
                            commit('unsetMapAtKey', category.id);
                            deleted.push(category);
                        }
                    });
                }

                return deleted;
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async addGuideToCategory ({ commit, dispatch, state }, payload = {}) {
            commit('setSaving', { saving: true });
            const { categoryId, guideId } = payload;

            if (!categoryId || !guideId) {
                commit('setSaving', { saving: false });

                return;
            }

            try {
                if (payload.oldCategoryId && state.map[payload.oldCategoryId]) {
                    const cleanedUpCategory = getCleanedUpCategory(state.map[payload.oldCategoryId], payload.guideId);
                    commit('setMapAtKey', {
                        guideCategory: Object.freeze(cleanedUpCategory),
                    });
                }
                await addGuideToCategory(payload);
                await dispatch('loadOne', { reFetch: true, items: true, categoryId });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setSaving', { saving: false });
            }
        },
        async removeGuideFromCategory ({ commit, state }, payload = {}) {
            commit('setSaving', { saving: true });
            const { categoryId, guideId } = payload;
            if (!categoryId || !guideId) {
                commit('setSaving', { saving: false });

                return;
            }

            try {
                await removeGuideFromCategory(payload);
                commit('setMapAtKey', {
                    guideCategory: Object.freeze(getCleanedUpCategory(state.map[payload.categoryId], payload.guideId)),
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setSaving', { saving: false });
            }
        },
    },
    getters: {
        list: (state) => Object.values(state.map),
        getGroupExpandedKeys: (state) => state.groupExpandedKeys,
        guideIdMapOfCategories: (state, getters) => {
            return getters.list.reduce((acc, category) => {
                category?.items.forEach((categoryItem) => {
                    if (categoryItem.itemType !== 'Guide') return;

                    acc[categoryItem.itemId] = {
                        id: category.id,
                        name: category.name,
                    };
                });

                return acc;
            }, {});
        },
        guideCategoryById: (state) => (categoryId) => state.map[categoryId],
        guideCategoryByGuideId: (state, getters) => ({ guideId }) => {
            if (!guideId) return undefined;

            return getters.list.find((category) => category.items.some((item) => item.itemId === guideId));
        },
        guideCategoriesGroupedByMetaGroup: (state, getters, rootState, rootGetters) => (platform) => {
            const vocEnabled = rootGetters['productLines/productLineByType']('voc').purchased;
            const shouldIncludeCategory = (category) => {
                if (!category) return false;
                if (category.attributes?.platform !== platform) return false;
                if (category.name === 'Idea Test' && !vocEnabled) return false;

                return true;
            };
            const groupedCategoryIds = new Set();

            const metaGroupsWithCategories = sortByName(rootGetters['metaGroups/list']).map((metaGroup) => {
                const items = metaGroup.items
                    .map((item) => {
                        groupedCategoryIds.add(item.itemId);

                        return state.map[item.itemId];
                    })
                    .filter(shouldIncludeCategory);

                return { ...metaGroup, items: sortByName(items) };
            });

            const unassignedCategories = getters.list.filter((category) =>
                !groupedCategoryIds.has(category.id) && shouldIncludeCategory(category));
            const unassignedMetaGroup = createUnassignedMetaGroup(unassignedCategories);

            metaGroupsWithCategories.push(unassignedMetaGroup);

            return metaGroupsWithCategories;
        },
        getGuideCategoryByName: (state, getters) => (name, platform = 'web') => {
            return getters.list.find((category) => category.name === name && category.attributes.platform === platform);
        },
    },
};

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise.promise = null;
    loadAllPromise.items = false;
}

export default guideCategories;
